import { toLocalizedLongDateTime } from '@allurion/utils';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { TrackedButton, TrackedPageHeader } from 'src/analytics/TrackedUI';
import { Seo } from 'src/components/Seo';
import { Loader } from 'src/components/ui/Loader';
import { useAppNavigate } from 'src/hooks/useAppNavigate';
import { useLocale } from 'src/hooks/useLocale';
import { useMessageNotifications } from 'src/hooks/useMessageNotifications';
import { CarrotDown } from 'src/icons/CarrotIcons';
import { CloseIcon } from 'src/icons/CloseIcon';
import { SunIcon } from 'src/icons/SunIcon';
import * as appRoutes from 'src/utils/appRoutes';
import { KEY_CHAT } from 'src/utils/constants';

import styles from './MessageNotificationsPage.module.scss';

export default function MessageNotificationsPage() {
  const intl = useIntl();
  const { isLoading, notifications, hasNextPage, loadMore, removeNotification, clearAll } =
    useMessageNotifications();

  const { toPatientPage, toPreviousPage } = useAppNavigate();
  const { locale } = useLocale();

  const navigateToPatientPage = (clinicId: number, patientId: number) =>
    toPatientPage(clinicId.toString(), patientId.toString(), KEY_CHAT);

  function isEmptyMessage(content: string | undefined) {
    return !content || content.trim().length === 0;
  }

  return (
    <div className={styles.container}>
      <Seo
        title={intl.formatMessage({ id: 'new-messages.title', defaultMessage: 'New Messages' })}
      />
      <TrackedPageHeader
        title={intl.formatMessage({
          id: 'new-messages.header',
          description: 'New messages page header',
          defaultMessage: 'New messages',
        })}
        button={{
          label: intl.formatMessage({
            id: 'new-messages.clear-all',
            defaultMessage: 'Clear all',
          }),
          variant: 'secondary',
          onClick: clearAll,
          trackLabel: 'clear-all-messages',
        }}
        onNavButtonClick={() => toPreviousPage()}
      />

      {isLoading && <Loader isLoading={isLoading} />}
      <div className={styles.innerContainer}>
        {notifications.length < 1 ? (
          <div className={styles.noMessages}>
            <SunIcon fill="#77989E" size="48" />
            <div>
              {intl.formatMessage({
                id: 'new-messages.no-messages',
                defaultMessage: 'Nothing new for now',
              })}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.messagesList}>
              {notifications.map((message) => (
                <div className={styles.message} key={message.message_id} data-cy="message">
                  <div
                    className={styles.notificationBody}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigateToPatientPage(message.clinic_id, message.patient_id)}
                    onKeyDown={() => navigateToPatientPage(message.clinic_id, message.patient_id)}
                  >
                    <div className={styles.name}>{message.patient_name}</div>
                    <div className={styles.content}>
                      {message.has_attachment && isEmptyMessage(message.content)
                        ? intl.formatMessage({
                            id: 'message-notification.sent-attachment',
                            defaultMessage: 'Sent an attachment',
                          })
                        : message.content}
                    </div>
                    <div className={styles.date}>
                      {toLocalizedLongDateTime(message.sent_date, locale)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.closeBtn}
                    onClick={() => removeNotification(message.message_id)}
                  >
                    <CloseIcon size="20" color="00363F" />
                  </button>
                </div>
              ))}
            </div>
            {hasNextPage && (
              <div className={styles.showMoreContainer}>
                <TrackedButton
                  variant="outlined"
                  label={intl.formatMessage({
                    id: 'new-messages.show-more',
                    defaultMessage: 'Show more',
                  })}
                  rightIcon={<CarrotDown />}
                  onClick={loadMore}
                  size="sm"
                  trackLabel="show-more-messages"
                />
              </div>
            )}

            <Link className={styles.styledLink} to={appRoutes.profileRoute}>
              {intl.formatMessage({
                id: 'new-messages.notifications-preferences-link',
                defaultMessage: 'Manage notifications preferences',
              })}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
